/**
 * @module OperationProgressHelpers
 * @purpose Pure functions for OperationProgress: percent maths and a generic,
 *   feature-agnostic reading of the `outcomeSummary` JSONB blob.
 * @depends None
 * @dependedOnBy OperationProgress, operation-progress.parts
 */

const PERCENT_MAX = 100
const PERCENT_MIN = 0

/**
 * @behaviour Clamps `unitsDone / unitsTotal` to a whole-number percentage.
 * @errors Returns 0 for a zero or negative total (avoids division artefacts).
 */
export function computePercent(unitsDone: number, unitsTotal: number): number {
  if (unitsTotal <= 0) return PERCENT_MIN
  const raw = Math.round((unitsDone / unitsTotal) * PERCENT_MAX)
  return Math.min(PERCENT_MAX, Math.max(PERCENT_MIN, raw))
}

export interface OutcomeSummary {
  primary: string | null
  entries: Array<{ label: string; value: string }>
}

const PRIMARY_SUMMARY_KEYS = ['error', 'failures'] as const

function stringifyValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.map(String).join(', ')
  if (value === null || value === undefined) return ''
  return JSON.stringify(value)
}

/**
 * @behaviour Reads the arbitrary `outcomeSummary` JSON generically: a known
 *   failure-cause key (`error`/`failures`) becomes the headline `primary`
 *   line, every other key becomes a label/value row. No feature-specific
 *   keys are assumed -- this stays a platform-wide reading, not a per-caller
 *   parser (FR16a).
 * @errors Returns an empty summary for non-object input.
 */
export function summariseOutcome(outcomeSummary: unknown): OutcomeSummary {
  if (typeof outcomeSummary !== 'object' || outcomeSummary === null) {
    return { primary: null, entries: [] }
  }
  const record = outcomeSummary as Record<string, unknown>
  const primaryKey = PRIMARY_SUMMARY_KEYS.find((key) => key in record)
  const primary = primaryKey ? stringifyValue(record[primaryKey]) : null
  const entries = Object.entries(record)
    .filter(([key]) => key !== primaryKey)
    .map(([label, value]) => ({ label, value: stringifyValue(value) }))
  return { primary, entries }
}
